import React, { useEffect, useMemo, useState } from "react";
import { UtensilsCrossed, GlassWater, Check, RefreshCw, Loader2 } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n || 0));
const monthKey = (iso) => (iso || "").slice(0, 7);
const monthLabel = (mk) => {
  const [y, m] = mk.split("-");
  const names = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  return `${names[Number(m) - 1]} ${y}`;
};

export default function MealOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = () => {
    setLoading(true);
    api.get("/admin/meal-orders").then((r) => setOrders(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const months = useMemo(() => {
    const set = new Set(orders.map((o) => monthKey(o.created_at)).filter(Boolean));
    set.add(new Date().toISOString().slice(0, 7));
    return Array.from(set).sort().reverse();
  }, [orders]);

  const monthOrders = useMemo(() => orders.filter((o) => monthKey(o.created_at) === month), [orders, month]);
  const caMonth = monthOrders.reduce((s, o) => s + Number(o.total || 0), 0);
  const caTraite = monthOrders.filter((o) => o.status === "traité").reduce((s, o) => s + Number(o.total || 0), 0);
  const pending = orders.filter((o) => o.status !== "traité").length;

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/meal-orders/${id}`, { status });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusyId(null); }
  };

  const svcBadge = (svc) => svc === "room_drinks"
    ? <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 ring-1 ring-sky-200 px-2.5 py-1 text-xs font-medium"><GlassWater size={13} /> Boisson en chambre</span>
    : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 px-2.5 py-1 text-xs font-medium"><UtensilsCrossed size={13} /> Sur place</span>;

  return (
    <div data-testid="meal-orders-tab">
      {/* Récap CA */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500 mb-1">CA du mois ({monthLabel(month)})</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="ca-month">{eur(caMonth)}</p>
          <p className="text-xs text-slate-400 mt-1">{monthOrders.length} commande{monthOrders.length > 1 ? "s" : ""}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500 mb-1">Dont traité</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="ca-traite">{eur(caTraite)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500 mb-1">À traiter (tous mois)</p>
          <p className="text-2xl font-bold text-brand" data-testid="pending-count">{pending}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="month-filter">
          {months.map((mk) => <option key={mk} value={mk}>{monthLabel(mk)}</option>)}
        </select>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="reload-orders"><RefreshCw size={15} /> Actualiser</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand" /></div>
      ) : monthOrders.length === 0 ? (
        <p className="text-center text-slate-400 py-16" data-testid="no-orders">Aucune commande pour {monthLabel(month)}.</p>
      ) : (
        <div className="space-y-3">
          {monthOrders.map((o) => (
            <div key={o.id} className={`rounded-2xl border p-4 ${o.status === "traité" ? "border-slate-200 bg-slate-50/60" : "border-brand/30 bg-white"}`} data-testid={`order-${o.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-slate-900">{o.name}</span>
                    <span className="text-sm text-slate-500">· Chambre {o.room}</span>
                    {o.date && <span className="inline-flex items-center rounded-full bg-brand text-white px-2.5 py-1 text-xs font-semibold" data-testid={`order-date-${o.id}`}>Le {new Date(o.date + "T12:00:00").toLocaleDateString("fr-FR")}</span>}
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 text-brand ring-1 ring-brand/20 px-2.5 py-1 text-xs font-medium">{o.menu === "galettes" ? "Galettes & Crêpes" : "La Cuisine de Mon Père"}</span>
                    {svcBadge(o.service)}
                    {o.service !== "room_drinks" && o.time ? <span className="text-xs text-slate-500">{o.time}</span> : <span className="text-xs text-slate-400">dès que possible</span>}
                  </div>
                  <p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString("fr-FR")}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-slate-900">{eur(o.total)}</p>
                  {o.status === "traité"
                    ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><Check size={14} /> Traité</span>
                    : <span className="text-xs font-medium text-amber-600">À traiter</span>}
                </div>
              </div>
              <ul className="mt-3 space-y-0.5 text-sm text-slate-600">
                {(o.lines || []).map((l, i) => <li key={i}>{l.qty}× {l.name} <span className="text-slate-400">· {eur(l.qty * l.price)}</span></li>)}
              </ul>
              {o.note ? <p className="mt-2 text-xs text-slate-500 italic">Note : {o.note}</p> : null}
              <div className="mt-3 flex justify-end">
                {o.status === "traité"
                  ? <button onClick={() => setStatus(o.id, "reçue")} disabled={busyId === o.id} className="text-xs text-slate-500 hover:text-slate-800 underline disabled:opacity-50" data-testid={`unmark-${o.id}`}>Annuler « traité »</button>
                  : <button onClick={() => setStatus(o.id, "traité")} disabled={busyId === o.id} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50" data-testid={`mark-${o.id}`}>
                      {busyId === o.id ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Marquer traité
                    </button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
